import { useState, useMemo, useEffect } from 'react';
import { useCampaignReport } from '@/hooks/useCampaignReport';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CustomerLink } from '@/components/customers/CustomerLink';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ChevronDown, ChevronRight, TrendingUp, TrendingDown, Download, Phone, DollarSign, CheckCircle, Briefcase, Users, ArrowUpDown, ArrowUp, ArrowDown, Info, X, XCircle, Percent, PiggyBank, Calendar } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { MetricCard, MetricComparison } from './MetricCard';
import { format, subDays, isToday, isYesterday } from 'date-fns';
import { useTimezoneUtils } from '@/hooks/useTimezoneUtils';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { useComparisonMetrics, ComparisonMetrics } from '@/hooks/useComparisonMetrics';
import { calculateChange } from '@/utils/periodComparison';

interface CampaignReportProps {
  startDate: Date;
  endDate: Date;
  timezone?: string;
  compareEnabled?: boolean;
}

export const CampaignReport = ({ startDate, endDate, timezone: propTimezone, compareEnabled = false }: CampaignReportProps) => {
  const { timezone: contextTimezone, formatDate } = useTimezoneUtils();
  const timezone = propTimezone || contextTimezone;
  const { formatMoney } = useCompanyLocale();
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [selectedSource, setSelectedSource] = useState<string>('all');
  const { data: campaigns, isLoading } = useCampaignReport(startDate, endDate, showActiveOnly, timezone);

  // Comparison data (deferred: loads after main data)
  const { data: comparisonData, isLoading: comparisonLoading } = useComparisonMetrics(
    startDate,
    endDate,
    timezone,
    !isLoading && !!campaigns && compareEnabled
  );

  const getComparison = (
    current: number,
    previousKey: keyof ComparisonMetrics,
    isPositiveGood = true
  ): MetricComparison | undefined => {
    if (!comparisonData || !compareEnabled) return undefined;
    const change = calculateChange(current, comparisonData.metrics[previousKey]);
    return { ...change, label: comparisonData.period.label, isPositiveGood };
  };

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<{ key: string | null; direction: 'asc' | 'desc' | null }>({ 
    key: null, 
    direction: null 
  });
  const [showDataDelayBanner, setShowDataDelayBanner] = useState(() => {
    return localStorage.getItem('hideCampaignDataDelayBanner') !== 'true';
  });

  // Calculate data freshness info
  const dataCurrentThrough = subDays(new Date(), 1);
  const isEndDateToday = isToday(endDate);
  const isEndDateYesterday = isYesterday(endDate);

  const dismissBanner = () => {
    setShowDataDelayBanner(false);
    localStorage.setItem('hideCampaignDataDelayBanner', 'true');
  };

  // Get unique lead sources from campaigns
  const leadSources = Array.from(
    new Set(campaigns?.map(c => c.lead_source_name).filter(Boolean))
  ).sort();

  // Handle sorting
  const handleSort = (key: string) => {
    setSortConfig(current => {
      if (current.key !== key) {
        return { key, direction: 'asc' };
      }
      if (current.direction === 'asc') {
        return { key, direction: 'desc' };
      }
      return { key: null, direction: null };
    });
  };

  const hasAnyData = (c: NonNullable<typeof campaigns>[number]) =>
    c.total_leads > 0 || c.total_jobs > 0 || c.actual_cost > 0 || c.total_revenue > 0;

  // Filter and sort campaigns — campaigns with data always come before empty ones
  const filteredCampaigns = useMemo(() => {
    const filtered = selectedSource === 'all'
      ? campaigns
      : campaigns?.filter(c => c.lead_source_name === selectedSource);

    if (!filtered) return filtered;

    return [...filtered].sort((a, b) => {
      const aHas = hasAnyData(a);
      const bHas = hasAnyData(b);
      if (aHas !== bHas) return aHas ? -1 : 1;

      if (!sortConfig.key || !sortConfig.direction) return 0;

      const aValue = a[sortConfig.key as keyof typeof a];
      const bValue = b[sortConfig.key as keyof typeof b];
      if (aValue === bValue) return 0;
      const comparison = aValue > bValue ? 1 : -1;
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [campaigns, selectedSource, sortConfig]);

  const PAGE_SIZE = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil((filteredCampaigns?.length || 0) / PAGE_SIZE));

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedSource, sortConfig, showActiveOnly, startDate, endDate]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const paginatedCampaigns = useMemo(() => {
    if (!filteredCampaigns) return filteredCampaigns;
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredCampaigns.slice(start, start + PAGE_SIZE);
  }, [filteredCampaigns, currentPage]);

  const getSortIcon = (key: string) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="h-3 w-3 ml-1 inline" />;
    if (sortConfig.direction === 'asc') return <ArrowUp className="h-3 w-3 ml-1 inline" />;
    return <ArrowDown className="h-3 w-3 ml-1 inline" />;
  };

  const handleExportCSV = () => {
    if (!filteredCampaigns) return;

    const headers = ['Campaign', 'Source', 'Tracking #', 'Active', 'Leads', 'Booked', 'Quoted', 'Closed', 'Cancelled', 'Paying Jobs %', 'Cost per Lead', 'Cost per Job', 'Turnover', 'Profit'];
    const rows = filteredCampaigns.map(c => [
      c.campaign_name,
      c.lead_source_name || 'N/A',
      c.tracking_number || '',
      c.is_active ? 'Yes' : 'No',
      c.total_leads,
      c.leads_converted,
      c.jobs_quoted,
      c.total_jobs,
      c.jobs_cancelled,
      c.paying_jobs_percentage.toFixed(1),
      c.cost_per_lead.toFixed(2),
      c.cost_per_job.toFixed(2),
      c.total_revenue.toFixed(2),
      c.campaign_profit.toFixed(2)
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `campaigns-${formatDate(startDate, 'yyyy-MM-dd')}-${formatDate(endDate, 'yyyy-MM-dd')}.csv`;
    a.click();
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!filteredCampaigns || filteredCampaigns.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-muted-foreground text-center">
            {selectedSource === 'all' 
              ? 'No campaign data available for this period.' 
              : `No campaigns found for ${selectedSource}.`}
          </p>
        </CardContent>
      </Card>
    );
  }

  const totalLeads = filteredCampaigns.reduce((sum, c) => sum + c.total_leads, 0);
  const totalBookings = filteredCampaigns.reduce((sum, c) => sum + c.total_bookings, 0);
  const totalJobs = filteredCampaigns.reduce((sum, c) => sum + c.total_jobs, 0);
  const totalCancelled = filteredCampaigns.reduce((sum, c) => sum + c.jobs_cancelled, 0);
  const totalPayingJobs = filteredCampaigns.reduce((sum, c) => sum + c.paying_jobs_count, 0);
  const payingJobsPercentage = totalJobs > 0 ? (totalPayingJobs / totalJobs) * 100 : 0;
  const totalCost = filteredCampaigns.reduce((sum, c) => sum + c.actual_cost, 0);
  const totalRevenue = filteredCampaigns.reduce((sum, c) => sum + c.total_revenue, 0);
  const totalProfit = filteredCampaigns.reduce((sum, c) => sum + c.campaign_profit, 0);
  const costPerLead = totalLeads > 0 ? totalCost / totalLeads : 0;
  const costPerJob = totalJobs > 0 ? totalCost / totalJobs : 0;

  return (
    <div className="space-y-6">
      {/* Data Delay Banner - Phase 6 */}
      {showDataDelayBanner && (
        <Alert className="relative border-info/30 bg-info/10">
          <Info className="h-4 w-4 text-info" />
          <AlertTitle className="text-info">Ad spend data updated daily at 2:00 AM</AlertTitle>
          <AlertDescription className="text-info">
            <span>Reports show data through </span>
            <strong>{format(dataCurrentThrough, 'EEEE, MMMM d, yyyy')}</strong>.
            {isEndDateToday && (
              <span className="ml-1">Today's spend will be available tomorrow at 2 AM.</span>
            )}
            {isEndDateYesterday && (
              <span className="ml-1">Yesterday's data should now be complete.</span>
            )}
          </AlertDescription>
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-2 right-2 h-6 w-6 text-info hover:text-info hover:bg-info/10"
            onClick={dismissBanner}
          >
            <X className="h-4 w-4" />
          </Button>
        </Alert>
      )}

      {/* Summary Cards - 3x3 Grid */}
      <div className="grid gap-4 md:grid-cols-3">
        <MetricCard
          title="Number of Leads"
          value={totalLeads.toLocaleString()}
          subtitle="total leads"
          icon={Users}
          comparison={getComparison(totalLeads, 'totalLeads', true)}
          comparisonLoading={compareEnabled && comparisonLoading}
        />
        <MetricCard
          title="Number of Bookings"
          value={totalBookings.toLocaleString()}
          subtitle="jobs created"
          icon={Calendar}
          comparison={getComparison(totalBookings, 'convertedLeads', true)}
          comparisonLoading={compareEnabled && comparisonLoading}
        />
        <MetricCard
          title="Number of Jobs"
          value={totalJobs.toLocaleString()}
          subtitle="completed"
          icon={CheckCircle}
          comparison={getComparison(totalJobs, 'completedJobs', true)}
          comparisonLoading={compareEnabled && comparisonLoading}
        />
        <MetricCard
          title="Jobs Cancelled"
          value={totalCancelled.toLocaleString()}
          subtitle="cancelled jobs"
          icon={XCircle}
          valueColor="text-destructive"
          comparison={getComparison(totalCancelled, 'cancelledJobs', false)}
          comparisonLoading={compareEnabled && comparisonLoading}
        />
        <MetricCard
          title="Paying Jobs %"
          value={`${payingJobsPercentage.toFixed(1)}%`}
          subtitle={`${totalPayingJobs} of ${totalJobs} jobs`}
          icon={Percent}
          valueColor={payingJobsPercentage >= 80 ? "text-success" : undefined}
        />
        <MetricCard
          title="Cost per Lead"
          value={formatMoney(costPerLead)}
          subtitle="ad spend / leads"
          icon={DollarSign}
        />
        <MetricCard
          title="Cost per Job"
          value={formatMoney(costPerJob)}
          subtitle="ad spend / completed"
          icon={DollarSign}
        />
        <MetricCard
          title="Turnover"
          value={formatMoney(totalRevenue)}
          subtitle="from completed jobs"
          icon={TrendingUp}
          comparison={getComparison(totalRevenue, 'totalTurnover', true)}
          comparisonLoading={compareEnabled && comparisonLoading}
        />
        <MetricCard
          title="Profit"
          value={formatMoney(totalProfit)}
          subtitle="after all costs incl. ads"
          icon={PiggyBank}
          valueColor={totalProfit >= 0 ? "text-success" : "text-destructive"}
          comparison={getComparison(totalProfit, 'netProfit', true)}
          comparisonLoading={compareEnabled && comparisonLoading}
        />
      </div>

      {/* Main Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-4 flex-wrap">
            <CardTitle>Campaign Performance</CardTitle>
            <div className="flex items-center gap-4">
              <div className="flex items-center space-x-2">
                <Switch id="active-only" checked={showActiveOnly} onCheckedChange={setShowActiveOnly} />
                <Label htmlFor="active-only" className="text-sm">Active only</Label>
              </div>
              <Select value={selectedSource} onValueChange={setSelectedSource}>
                <SelectTrigger className="w-[180px] h-9">
                  <SelectValue placeholder="All Sources" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  {leadSources.map(source => (
                    <SelectItem key={source} value={source}>{source}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleExportCSV}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Campaign</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Tracking #</TableHead>
                  <TableHead
                    className="text-right cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('conversion_rate')}
                  >
                    Book % {getSortIcon('conversion_rate')}
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('total_leads')}
                  >
                    Leads {getSortIcon('total_leads')}
                  </TableHead>
                  <TableHead
                    className="text-right cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('leads_converted')}
                  >
                    Booked {getSortIcon('leads_converted')}
                  </TableHead>
                  <TableHead
                    className="text-right cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('jobs_quoted')}
                  >
                    Quoted {getSortIcon('jobs_quoted')}
                  </TableHead>
                  <TableHead
                    className="text-right cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('total_jobs')}
                  >
                    Closed {getSortIcon('total_jobs')}
                  </TableHead>
                  <TableHead
                    className="text-right cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('jobs_cancelled')}
                  >
                    Cancelled {getSortIcon('jobs_cancelled')}
                  </TableHead>
                  <TableHead
                    className="text-right cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('actual_cost')}
                  >
                    Total Cost {getSortIcon('actual_cost')}
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('total_revenue')}
                  >
                    Revenue {getSortIcon('total_revenue')}
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('cost_per_acquisition')}
                  >
                    CPA {getSortIcon('cost_per_acquisition')}
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('return_on_ad_spend')}
                  >
                    ROAS {getSortIcon('return_on_ad_spend')}
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => handleSort('campaign_profit')}
                  >
                    Profit {getSortIcon('campaign_profit')}
                  </TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedCampaigns?.map(campaign => (
                  <>
                    <TableRow key={campaign.campaign_id} className="cursor-pointer hover:bg-muted/50" onClick={() => setExpandedId(expandedId === campaign.campaign_id ? null : campaign.campaign_id)}>
                      <TableCell>
                        {expandedId === campaign.campaign_id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </TableCell>
                      <TableCell className="font-medium max-w-[200px]">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="truncate block">{campaign.campaign_name}</span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{campaign.campaign_name}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell>
                        {campaign.lead_source_name && (
                          <Badge variant="outline" className="text-xs">
                            {campaign.lead_source_name}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {campaign.tracking_number && (
                          <Badge variant="outline" className="font-mono text-xs">
                            <Phone className="h-3 w-3 mr-1" />
                            {campaign.tracking_number}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <span className={campaign.conversion_rate >= 50 ? 'text-success font-semibold' : campaign.conversion_rate >= 25 ? 'text-info' : 'text-muted-foreground'}>
                            {campaign.conversion_rate.toFixed(1)}%
                          </span>
                          {campaign.conversion_rate >= 50 ? <TrendingUp className="h-3 w-3 text-success" /> : campaign.conversion_rate < 10 ? <TrendingDown className="h-3 w-3 text-destructive" /> : null}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium">{campaign.total_leads}</TableCell>
                      <TableCell className="text-right">
                        <span className="text-success font-medium">{campaign.leads_converted}</span>
                      </TableCell>
                      <TableCell className="text-right text-info">{campaign.jobs_quoted}</TableCell>
                      <TableCell className="text-right font-medium">{campaign.total_jobs}</TableCell>
                      <TableCell className="text-right text-destructive">{campaign.jobs_cancelled}</TableCell>
                      <TableCell className="text-right font-medium">
                        {formatMoney(campaign.actual_cost)}
                      </TableCell>
                      <TableCell className="text-right font-medium">{formatMoney(campaign.total_revenue)}</TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {campaign.cost_per_acquisition ? formatMoney(campaign.cost_per_acquisition) : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        {campaign.return_on_ad_spend ? (
                          <span className={campaign.return_on_ad_spend >= 2 ? 'text-success font-semibold' : 'text-muted-foreground'}>
                            {campaign.return_on_ad_spend.toFixed(2)}x
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={campaign.campaign_profit >= 0 ? 'text-success font-semibold' : 'text-destructive font-semibold'}>
                          {formatMoney(campaign.campaign_profit)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={campaign.is_active ? 'default' : 'secondary'}>
                          {campaign.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                    {expandedId === campaign.campaign_id && (
                      <TableRow>
                        <TableCell colSpan={16} className="bg-muted/20">
                          <div className="p-4 space-y-4">
                            <div className="grid gap-4 md:grid-cols-4">
                              <div>
                                <h4 className="text-sm font-semibold mb-2">Campaign Details</h4>
                                <div className="space-y-1 text-sm">
                                  {campaign.external_campaign_id && (
                                    <div className="flex justify-between">
                                      <span className="text-muted-foreground">External ID:</span>
                                      <span className="font-mono text-xs">{campaign.external_campaign_id}</span>
                                    </div>
                                  )}
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Webhook Leads:</span>
                                    <span className="font-medium">{campaign.webhook_leads}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Manual Leads:</span>
                                    <span className="font-medium">{campaign.manual_leads}</span>
                                  </div>
                                  {campaign.avg_call_duration && (
                                    <div className="flex justify-between">
                                      <span className="text-muted-foreground">Avg Call:</span>
                                      <span className="font-medium">{Math.round(campaign.avg_call_duration)}s</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div>
                                <h4 className="text-sm font-semibold mb-2">Performance</h4>
                                <div className="space-y-1 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Book Rate:</span>
                                    <span className="font-medium">{campaign.conversion_rate.toFixed(1)}%</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Loss Rate:</span>
                                    <span className="font-medium">{campaign.loss_rate.toFixed(1)}%</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Open Leads:</span>
                                    <span className="font-medium">{campaign.leads_open}</span>
                                  </div>
                                </div>
                              </div>
                              <div>
                                <h4 className="text-sm font-semibold mb-2">Revenue</h4>
                                <div className="space-y-1 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Total:</span>
                                    <span className="font-medium">{formatMoney(campaign.total_revenue)}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Avg Job:</span>
                                    <span className="font-medium">{formatMoney(campaign.avg_job_value)}</span>
                                  </div>
                                  {campaign.budget && (
                                    <div className="flex justify-between">
                                      <span className="text-muted-foreground">Budget:</span>
                                      <span className="font-medium">{formatMoney(campaign.budget)}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div>
                                <h4 className="text-sm font-semibold mb-2">Top CSRs</h4>
                                <div className="space-y-1 text-sm">
                                  {campaign.top_csrs.length > 0 ? (
                                    campaign.top_csrs.map((csr, idx) => (
                                      <div key={idx} className="flex justify-between">
                                        <span className="text-muted-foreground truncate max-w-[120px]">{csr.csr_name}</span>
                                        <span className="font-medium">{csr.lead_count}</span>
                                      </div>
                                    ))
                                  ) : (
                                    <span className="text-muted-foreground text-xs">No CSR data</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            {campaign.recent_conversions.length > 0 && (
                              <div>
                                <h4 className="text-sm font-semibold mb-2">Recent Conversions</h4>
                                <div className="space-y-1 text-sm">
                                  {campaign.recent_conversions.slice(0, 5).map(lead => (
                                    <div key={lead.id} className="flex justify-between items-center">
                                      <span className="text-muted-foreground">
                                        {lead.lead_number} -{' '}
                                        <CustomerLink customerId={lead.customer_id} name={lead.customer_name} />
                                      </span>
                                      <span className="font-medium">{formatMoney(lead.job_revenue)}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ))}
              </TableBody>
            </Table>
          </div>
          {filteredCampaigns.length > PAGE_SIZE && (
            <div className="flex items-center justify-between mt-4 text-sm">
              <span className="text-muted-foreground">
                Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filteredCampaigns.length)} of {filteredCampaigns.length}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <span className="text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
